import React, { useEffect, useRef, useState } from "react";
import { Plus, Trash2, Pencil, Upload, Download, Users as UsersIcon, Layers3, FileBox, ScrollText, ShieldCheck } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import PageHeader from "@/components/PageHeader";
import { useLang } from "@/context/LangContext";
import { toast } from "sonner";
import api, { API, formatApiErrorDetail } from "@/lib/api";
import { fmtBytes } from "@/lib/format";

const ROLES = ["admin", "user"];

export default function Admin() {
  const { t } = useLang();
  return (
    <div className="space-y-6 fade-up" data-testid="admin-page">
      <PageHeader title={t("administration")} subtitle={t("admin_only")} screen="administration"
        getContext={() => ({ area: "administration" })} />
      <Card className="p-2 sm:p-4 rounded-2xl shadow-sm">
        <Tabs defaultValue="users">
          <TabsList className="grid grid-cols-4 w-full max-w-2xl rounded-xl">
            <TabsTrigger value="users" data-testid="tab-users" className="gap-1.5"><UsersIcon className="h-4 w-4" /><span className="hidden sm:inline">{t("users")}</span></TabsTrigger>
            <TabsTrigger value="groups" data-testid="tab-groups" className="gap-1.5"><Layers3 className="h-4 w-4" /><span className="hidden sm:inline">{t("groups")}</span></TabsTrigger>
            <TabsTrigger value="content" data-testid="tab-content" className="gap-1.5"><FileBox className="h-4 w-4" /><span className="hidden sm:inline">{t("content")}</span></TabsTrigger>
            <TabsTrigger value="activity" data-testid="tab-activity" className="gap-1.5"><ScrollText className="h-4 w-4" /><span className="hidden sm:inline">{t("activity")}</span></TabsTrigger>
          </TabsList>
          <TabsContent value="users" className="mt-4"><UsersTab /></TabsContent>
          <TabsContent value="groups" className="mt-4"><GroupsTab /></TabsContent>
          <TabsContent value="content" className="mt-4"><ContentTab /></TabsContent>
          <TabsContent value="activity" className="mt-4"><ActivityTab /></TabsContent>
        </Tabs>
      </Card>
    </div>
  );
}

function UsersTab() {
  const { t } = useLang();
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "user", group: "", status: "active" });

  const load = () => { api.get("/users").then(({ data }) => setUsers(data)); api.get("/groups").then(({ data }) => setGroups(data)); };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm({ name: "", email: "", password: "", role: "user", group: "", status: "active" }); setOpen(true); };
  const openEdit = (u) => { setEditing(u); setForm({ name: u.name, email: u.email, password: "", role: u.role, group: u.group || "", status: u.status }); setOpen(true); };

  const save = async () => {
    try {
      if (editing) await api.put(`/users/${editing.id}`, form);
      else await api.post("/users", form);
      setOpen(false); load(); toast.success(t("save"));
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const del = async (u) => { try { await api.delete(`/users/${u.id}`); load(); toast.success(t("delete")); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } };

  return (
    <div className="space-y-4">
      <div className="flex justify-end"><Button onClick={openNew} data-testid="add-user-btn" className="rounded-xl gap-2"><Plus className="h-4 w-4" />{t("add_user")}</Button></div>
      <div className="overflow-x-auto rounded-xl border">
        <table className="w-full text-sm">
          <thead className="bg-secondary/60 text-xs uppercase tracking-wider text-muted-foreground">
            <tr><th className="text-start font-semibold px-4 py-3">{t("name")}</th><th className="text-start font-semibold px-4 py-3 hidden sm:table-cell">{t("email")}</th><th className="text-start font-semibold px-4 py-3">{t("role")}</th><th className="text-start font-semibold px-4 py-3 hidden md:table-cell">{t("group")}</th><th className="px-4 py-3 text-end">{t("actions_col")}</th></tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-t" data-testid={`user-row-${u.email}`}>
                <td className="px-4 py-3 font-medium">{u.name}</td>
                <td className="px-4 py-3 text-muted-foreground hidden sm:table-cell">{u.email}</td>
                <td className="px-4 py-3"><span className="rounded-full bg-primary/10 text-primary px-2.5 py-1 text-xs font-semibold capitalize">{u.role.replace("_", " ")}</span></td>
                <td className="px-4 py-3 text-muted-foreground hidden md:table-cell">{u.group || "-"}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-1">
                    <button onClick={() => openEdit(u)} data-testid={`edit-user-${u.email}`} className="p-2 rounded-lg hover:bg-secondary text-muted-foreground hover:text-foreground"><Pencil className="h-4 w-4" /></button>
                    <button onClick={() => del(u)} data-testid={`delete-user-${u.email}`} className="p-2 rounded-lg hover:bg-secondary text-muted-foreground hover:text-destructive"><Trash2 className="h-4 w-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="user-dialog">
          <DialogHeader><DialogTitle>{editing ? t("edit") : t("add_user")}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label>{t("name")}</Label><Input data-testid="user-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-xl" /></div>
            <div className="space-y-2"><Label>{t("email")}</Label><Input data-testid="user-email" type="email" disabled={!!editing} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-xl" /></div>
            <div className="space-y-2"><Label>{t("password")}{editing && <span className="text-xs text-muted-foreground"> (leave blank to keep)</span>}</Label><Input data-testid="user-password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="rounded-xl" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>{t("role")}</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}><SelectTrigger data-testid="user-role" className="rounded-xl"><SelectValue /></SelectTrigger>
                  <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r} className="capitalize">{r.replace("_", " ")}</SelectItem>)}</SelectContent></Select>
              </div>
              <div className="space-y-2"><Label>{t("group")}</Label>
                <Select value={form.group || "none"} onValueChange={(v) => setForm({ ...form, group: v === "none" ? "" : v })}><SelectTrigger data-testid="user-group" className="rounded-xl"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="none">-</SelectItem>{groups.map((g) => <SelectItem key={g.id} value={g.name}>{g.name}</SelectItem>)}</SelectContent></Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-xl">{t("cancel")}</Button>
            <Button onClick={save} data-testid="save-user-btn" className="rounded-xl">{t("save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function GroupsTab() {
  const { t } = useLang();
  const [groups, setGroups] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", name_ar: "", description: "" });
  const load = () => api.get("/groups").then(({ data }) => setGroups(data));
  useEffect(() => { load(); }, []);
  const save = async () => { if (!form.name) return; try { await api.post("/groups", form); setOpen(false); setForm({ name: "", name_ar: "", description: "" }); load(); toast.success(t("save")); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } };
  const del = async (g) => { await api.delete(`/groups/${g.id}`); load(); toast.success(t("delete")); };

  return (
    <div className="space-y-4">
      <div className="flex justify-end"><Button onClick={() => setOpen(true)} data-testid="add-group-btn" className="rounded-xl gap-2"><Plus className="h-4 w-4" />{t("add_group")}</Button></div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {groups.map((g) => (
          <Card key={g.id} className="p-5 rounded-2xl shadow-sm" data-testid={`group-card-${g.name}`}>
            <div className="flex items-start justify-between">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-accent/15 text-amber-600"><Layers3 className="h-5 w-5" /></div>
              <button onClick={() => del(g)} data-testid={`delete-group-${g.name}`} className="p-2 rounded-lg hover:bg-secondary text-muted-foreground hover:text-destructive"><Trash2 className="h-4 w-4" /></button>
            </div>
            <p className="font-semibold mt-3">{g.name}</p>
            <p className="text-xs text-muted-foreground">{g.description}</p>
            <p className="text-xs font-medium mt-3">{g.members} {t("members")}</p>
          </Card>
        ))}
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="group-dialog">
          <DialogHeader><DialogTitle>{t("add_group")}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label>{t("name")}</Label><Input data-testid="group-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-xl" /></div>
            <div className="space-y-2"><Label>Description</Label><Input data-testid="group-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-xl" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-xl">{t("cancel")}</Button>
            <Button onClick={save} data-testid="save-group-btn" className="rounded-xl">{t("save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ContentTab() {
  const { t } = useLang();
  const [items, setItems] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const load = () => api.get("/admin/content").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);

  const upload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    const form = new FormData(); form.append("file", file);
    try { await api.post("/admin/upload", form, { headers: { "Content-Type": "multipart/form-data" } }); load(); toast.success(t("upload_content")); }
    catch { toast.error("Upload failed"); } finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const download = async (item) => {
    try {
      const res = await api.get(`/admin/download/${item.id}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = item.filename; a.click();
    } catch { toast.error("Download failed"); }
  };

  return (
    <div className="space-y-4">
      <Card className="p-4 rounded-xl border-amber-500/40 bg-amber-500/10 flex items-center gap-2">
        <ShieldCheck className="h-4 w-4 text-amber-600 shrink-0" /><p className="text-sm font-medium text-amber-800 dark:text-amber-300">{t("admin_only")}</p>
      </Card>
      <div className="flex justify-end">
        <input ref={fileRef} type="file" hidden onChange={upload} data-testid="content-file-input" />
        <Button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="upload-content-btn" className="rounded-xl gap-2"><Upload className="h-4 w-4" />{uploading ? "..." : t("upload_content")}</Button>
      </div>
      <div className="overflow-x-auto rounded-xl border">
        <table className="w-full text-sm">
          <thead className="bg-secondary/60 text-xs uppercase tracking-wider text-muted-foreground">
            <tr><th className="text-start font-semibold px-4 py-3">{t("filename")}</th><th className="text-start font-semibold px-4 py-3">{t("size")}</th><th className="text-start font-semibold px-4 py-3 hidden sm:table-cell">{t("uploaded_by")}</th><th className="px-4 py-3 text-end">{t("actions_col")}</th></tr>
          </thead>
          <tbody>
            {items.map((it) => (
              <tr key={it.id} className="border-t" data-testid={`content-row-${it.filename}`}>
                <td className="px-4 py-3 font-medium">{it.filename}</td>
                <td className="px-4 py-3 text-muted-foreground">{fmtBytes(it.size)}</td>
                <td className="px-4 py-3 text-muted-foreground hidden sm:table-cell">{it.uploaded_by}</td>
                <td className="px-4 py-3 text-end"><Button size="sm" variant="outline" onClick={() => download(it)} data-testid={`download-${it.filename}`} className="rounded-lg gap-1.5"><Download className="h-3.5 w-3.5" />{t("download")}</Button></td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={4} className="px-4 py-10 text-center text-muted-foreground">No content uploaded yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ActivityTab() {
  const [logs, setLogs] = useState([]);
  useEffect(() => { api.get("/activity-logs").then(({ data }) => setLogs(data)); }, []);
  return (
    <div className="overflow-x-auto rounded-xl border">
      <table className="w-full text-sm">
        <thead className="bg-secondary/60 text-xs uppercase tracking-wider text-muted-foreground">
          <tr><th className="text-start font-semibold px-4 py-3">User</th><th className="text-start font-semibold px-4 py-3">Action</th><th className="text-start font-semibold px-4 py-3 hidden sm:table-cell">Detail</th><th className="text-start font-semibold px-4 py-3 hidden md:table-cell">Time</th></tr>
        </thead>
        <tbody>
          {logs.map((l, i) => (
            <tr key={i} className="border-t" data-testid={`activity-row-${i}`}>
              <td className="px-4 py-3 font-medium">{l.user_email}</td>
              <td className="px-4 py-3"><span className="rounded-full bg-secondary px-2 py-0.5 text-xs font-semibold">{l.action}</span></td>
              <td className="px-4 py-3 text-muted-foreground hidden sm:table-cell">{l.detail}</td>
              <td className="px-4 py-3 text-muted-foreground hidden md:table-cell">{new Date(l.timestamp).toLocaleString()}</td>
            </tr>
          ))}
          {logs.length === 0 && <tr><td colSpan={4} className="px-4 py-10 text-center text-muted-foreground">No activity yet.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}
